import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Heart, Users, Shield, Globe } from 'lucide-react';
import CTA from '../../components/CTA';

const PortrushHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Portrush | Performance Anxiety & Seasonal Stress</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Portrush hospitality workers and event staff. Manage NW200 performance anxiety, seasonal employment stress, and tourism industry burnout. CNHC registered. Belfast clinic and online sessions available."
        />
        <meta name="keywords" content="hypnotherapy Portrush, hypnotherapist Portrush, performance anxiety Portrush, NW200 stress, hospitality worker burnout, seasonal anxiety, tourism stress Causeway Coast, event anxiety, therapist Portrush" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/portrush-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Portrush | Performance Anxiety & Seasonal Stress" />
        <meta property="og:description" content="Professional hypnotherapy for Portrush. Manage event performance anxiety, seasonal stress, hospitality burnout. Belfast clinic and online sessions available." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Portrush",
            "description": "CNHC registered hypnotherapy serving Portrush and the Causeway Coast. Specializing in performance anxiety for events (NW200, Open Championship), hospitality worker burnout, seasonal employment stress, and tourism industry mental health. In-person sessions available at our Belfast clinic or online sessions for ultimate flexibility.",
            "url": "https://www.sharonclare-hypnotherapy.com/portrush-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Portrush, County Antrim",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Performance Anxiety Treatment",
              "Event Stress Management",
              "Hospitality Worker Burnout Support",
              "Seasonal Employment Anxiety",
              "Tourism Industry Mental Health"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy for Portrush
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Performance anxiety, seasonal stress, and hospitality burnout support
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Clock className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  Online sessions from home, or in person at the Belfast and Lisburn clinics
                </p>
              </div>
              <p className="text-gray-600">
                Secure video sessions give you maximum flexibility during the busy tourism season. In-person appointments are available at Belfast Wellness Clinic on the Ormeau Road and at The Arzen Collective in Lisburn.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Portrush's Tourism and Events Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Portrush is Northern Ireland's events capital - home to the NW200 motorcycle race and the Open Championship at Royal Portrush Golf Club. This brings global recognition, economic opportunity, and intense performance pressure for everyone working in hospitality, tourism, and retail. As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand the unique mental health challenges of working in a high-intensity seasonal resort where the world's eyes are regularly on your town.
              </p>
              <p>
                For Portrush residents, <strong>online sessions via secure video call</strong> are usually the most practical way to work together — no travel time, and appointments that suit shift workers with unpredictable schedules. Whether you're managing performance anxiety in the lead-up to NW200, recovering from hospitality burnout after another exhausting summer season, or dealing with financial stress during the quiet winter months, support is available without leaving home. If you would rather meet face to face, I hold clinics at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, where days and times vary and early evening appointments are available, and at The Arzen Collective, 13B Antrim Street, Lisburn.
              </p>
              <p>
                Working in Portrush's tourism industry means high stakes - one bad review during the Open Championship can impact your business for years. Kitchen staff working 16-hour days during race week. Bar staff managing international crowds. Retail workers facing customer expectations when global media is in town. This isn't ordinary work stress - it's performance anxiety in front of an audience of millions. And then there's the financial reality: earning most of your annual income in 4-5 summer months, then managing finances through 7-8 months of quiet season when work dries up. It's exhausting, financially precarious, and takes a toll on mental health and relationships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Portrush Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps Portrush's Tourism Workers
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Users className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Event Performance Anxiety</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      The NW200 brings 150,000 spectators to Portrush, Portstewart, and Coleraine. The Open Championship puts Royal Portrush on international TV screens. If you're working hospitality, retail, or event support services during these major events, the pressure to perform flawlessly is intense. One mistake could be captured on camera, spread on social media, or cost your employer thousands in negative publicity. This isn't paranoia - it's the reality of working in a small town hosting world-class events.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Hypnotherapy addresses performance anxiety at its root by calming your nervous system's fight-or-flight response. Through deep relaxation and targeted suggestion work, we build confidence, reduce anticipatory anxiety, and help you stay focused and composed during high-pressure periods. You'll still work hard, but you'll manage the mental load without constant dread or sleep deprivation in the weeks leading up to race weekend or tournament dates.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Heart className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Hospitality Worker Burnout</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      If you're working in Portrush hospitality - hotels, restaurants, Barry's Amusements, bars, cafes - you know the toll it takes. Long shifts on your feet, unsociable hours that wreck your sleep patterns, dealing with difficult customers when you're exhausted, and the emotional labour of maintaining a cheerful demeanor when you're running on empty. The summer season is relentless: May through September with barely a break, working 12-16 hour days during peak weeks, then facing the sudden drop to minimal hours once September ends.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Burnout isn't weakness - it's your body and mind signaling they've exceeded capacity. Hypnotherapy helps you build resilience to handle the intense summer workload without complete exhaustion, improve sleep quality despite shift work, and establish boundaries so you're not depleted by constant emotional labor. We work on stress management techniques that fit your lifestyle, addressing the physical tension, racing thoughts, and fatigue that come with hospitality work. It's practical support for a demanding industry.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Seasonal Employment Financial Stress</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      The harsh reality of Portrush's seasonal economy: you might earn most of your year's income during the busy summer months (May-September), then face 7-8 months of reduced hours, zero-hours contract uncertainty, or signing on for Universal Credit. You're expected to budget summer earnings to last through winter, pay rent and mortgage every month regardless of income fluctuation, and feel constant anxiety about whether you've saved enough. Many Portrush workers juggle multiple jobs to make ends meet year-round, adding to exhaustion and stress.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Financial stress keeps your nervous system in constant high alert, affecting sleep, relationships, and decision-making. Hypnotherapy can't change the seasonal employment structure, but it can reduce the mental and physical impact of financial anxiety. We work on calming the stress response that makes budgeting feel overwhelming, addressing the catastrophic thinking that spirals into panic attacks, and building the mental resilience to navigate uncertainty without it consuming your entire life. You deserve support for managing a fundamentally precarious economic situation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Shield className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Sleep Problems & Exhaustion Management</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Shift work in Portrush's hospitality sector wreaks havoc on sleep patterns. Late bar shifts finishing at 2am, then early breakfast shifts starting at 6am. Split shifts where you work morning, have a few hours off, then work evening - never really resting. Racing thoughts keeping you awake after high-stress service periods. Physical exhaustion but mental overstimulation preventing sleep. The cumulative sleep deprivation over a busy summer can leave you feeling like you're functioning at 50% capacity.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Hypnotherapy is particularly effective for sleep issues because it directly addresses the hyperarousal state that prevents quality rest. We teach your nervous system to shift from high-alert mode to calm-rest mode, even after stressful shifts. Through relaxation techniques and targeted hypnotic suggestion, we retrain your sleep patterns so you can fall asleep faster, stay asleep longer, and wake feeling more rested. Better sleep improves everything - mood, concentration, immune function, and ability to cope with stress.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Why Portrush Residents Choose My Practice
            </h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Sessions That Fit Around Portrush Life</h3>
                  <p className="text-gray-700">
                    Online sessions mean no travel, so appointments stay practical even during your busiest season. If you prefer face to face, the Belfast clinic on the Ormeau Road and the Lisburn clinic on Antrim Street are both available, with times that vary.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Online Sessions for Shift Workers</h3>
                  <p className="text-gray-700">
                    Unpredictable hospitality schedules? Online sessions via secure video call mean you can attend from home at times that actually work for you - early morning before your lunch shift, late evening after closing, or whenever your rota allows. No travel time means no stress about making it to appointments.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">15 Years Belfast City Hospital Nursing Experience</h3>
                  <p className="text-gray-700">
                    I spent 15 years as a nurse at Belfast City Hospital before specializing in hypnotherapy. This healthcare background means I understand shift work exhaustion, burnout, performance pressure, and the physical toll of demanding work. I've worked in high-stress medical environments where mistakes have serious consequences - I understand performance anxiety at a deep level.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">CNHC Registered & Professionally Qualified</h3>
                  <p className="text-gray-700">
                    CNHC registration means I've completed rigorous training (450+ hours minimum), hold professional indemnity insurance, and adhere to strict ethical standards. You're working with a credentialed practitioner, not someone who did a weekend course. This matters for your safety and treatment quality.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Transparent Pricing - No Hidden Costs</h3>
                  <p className="text-gray-700">
                    Book a single session or a discounted 4-session package. No upselling, no pressure to buy endless packages. I understand financial stress is real in seasonal work - you deserve honest pricing and the flexibility to book what you can afford.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Portrush Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Can hypnotherapy help with NW200 race week anxiety?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes. NW200 brings massive crowds, international attention, and intense pressure for Portrush hospitality and retail workers. Hypnotherapy reduces anticipatory anxiety in the weeks leading up to race weekend, helps you stay calm and focused during the actual event, and addresses the post-event exhaustion. We work on performance confidence, stress management, and resilience building so you can handle race week without complete burnout.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">I work split shifts and irregular hours - how do I fit in sessions?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Online sessions are ideal for shift workers. Book appointments early morning, late evening, or whenever your schedule allows - no travel time means maximum flexibility. I also offer in-person sessions at our Belfast clinic if you prefer face-to-face and can fit the drive around your rota. We find a solution that works for your hospitality schedule.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Will hypnotherapy help me sleep better after late bar shifts?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Absolutely. Shift work disrupts natural sleep patterns, and late shifts leave you wired when you need to wind down. Hypnotherapy teaches your nervous system to shift from high-alert mode to rest mode, helping you fall asleep faster and sleep more deeply despite irregular hours. Many shift workers report significant improvement in sleep quality after just a few sessions. Better sleep means better mood, concentration, and ability to cope with demanding work.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Can you help with financial anxiety about quiet winter months?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes. Seasonal employment creates genuine financial stress - earning most income in 4-5 summer months, then managing 7-8 months of reduced work. Hypnotherapy can't change the economic reality, but it reduces the mental and physical impact of financial anxiety. We work on calming the constant worry, addressing catastrophic thinking that triggers panic, and building resilience to navigate uncertainty. You'll still budget carefully, but without the debilitating stress response.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">How does hypnotherapy help with hospitality burnout?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Hospitality burnout results from chronic stress, emotional labour, physical exhaustion, and lack of recovery time. Hypnotherapy addresses the nervous system dysregulation underlying burnout - the constant fight-or-flight activation that depletes your energy. We work on stress resilience, boundary setting, emotional regulation, and nervous system calming. It's not about working harder - it's about recovering better so the demanding work doesn't destroy your health.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">I'm anxious about performing badly during the Open Championship - can you help?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Absolutely. The Open Championship at Royal Portrush brings unprecedented global attention. If you're working hospitality, retail, or support services, the stakes feel impossibly high. Hypnotherapy builds performance confidence, reduces anticipatory anxiety, and helps you stay focused and calm under pressure. We use techniques proven in elite sports psychology, adapted for the high-pressure environment of serving international VIP clients when the world is watching. You'll perform better, not because you're trying harder, but because you're managing the mental load effectively.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Can I afford hypnotherapy on zero-hours contract wages?</h3>
                <p className="text-gray-700 leading-relaxed">
                  You can book single sessions or a discounted 4-session package. Many clients find 3-4 sessions makes a significant difference for acute stress or specific anxiety. No pressure to commit to expensive long-term packages - you choose what's affordable. If the fee is genuinely out of reach, contact me - I may be able to offer concessionary rates for those in financial hardship. Mental health support shouldn't be luxury-only.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Will my employer find out I'm seeing a hypnotherapist?</h3>
                <p className="text-gray-700 leading-relaxed">
                  No. Complete confidentiality is guaranteed by CNHC ethical standards - I cannot share your information with employers, family, or anyone else without your explicit consent. Online sessions mean total discretion (no one sees you arriving at a clinic). Small-town privacy concerns are valid; your mental health support is private. The only exception is if you disclose immediate risk of serious harm to yourself or others, where legal duty of care requires appropriate action.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">How long does treatment take? I can't commit to months of weekly sessions.</h3>
                <p className="text-gray-700 leading-relaxed">
                  Many clients see improvement in 3-6 sessions over 4-8 weeks. Acute stress or specific anxiety (e.g., NW200 performance anxiety) may resolve in 3-4 sessions. Chronic burnout or long-term sleep issues may need 6-8 sessions. We review progress regularly and adjust frequency based on your goals, budget, and schedule. There's no pressure for endless long-term therapy - we work efficiently toward specific outcomes.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">What if I can't relax during hypnosis because I'm too stressed?</h3>
                <p className="text-gray-700 leading-relaxed">
                  If you're chronically stressed from hospitality work, your nervous system is stuck in high-alert mode. Ironically, this means you need hypnotherapy's relaxation benefits most - but it may take a few sessions to teach your body how to relax again. We start gently, building your capacity for relaxation gradually. Most people are surprised how quickly they learn to access deep calm, even if they initially thought "I'm too stressed to relax." It's a skill that improves with practice, and the early sessions lay the foundation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What Causeway Coast Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "I work hospitality in Portrush and was dreading another NW200 after last year's complete burnout. Sharon helped me manage the anxiety before race week and I actually coped well this time. Still exhausting, but I didn't have panic attacks or call in sick. Massive difference."
                </p>
                <p className="text-sm font-semibold text-gray-900">— K.M., North Coast hospitality worker</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "Seasonal work stress was ruining my life - earning summer wages then panicking all winter about money. Hypnotherapy didn't magically fix the economic situation but it stopped the constant catastrophic thinking. I can budget now without spiraling into anxiety attacks."
                </p>
                <p className="text-sm font-semibold text-gray-900">— T.L., Causeway Coast retail</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "Late bar shifts destroyed my sleep. I'd finish at 2am, get home wired, lie awake until 5am, then drag through the next day exhausted. After working with Sharon I learned how to wind down properly. Sleep improved dramatically within a few weeks."
                </p>
                <p className="text-sm font-semibold text-gray-900">— R.D., County Antrim</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "Online sessions were perfect for my unpredictable shifts. Could book early morning or late evening appointments without driving anywhere. Sharon understood hospitality stress from her nursing background - she gets it. Practical support that actually worked around my life."
                </p>
                <p className="text-sm font-semibold text-gray-900">— H.S., Portrush area</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "I was burning out from tourism work - no work-life balance, exhausted all the time, drinking too much to cope. Hypnotherapy helped me set boundaries and recover better between shifts. Still work hard but I'm not destroying my health anymore. Actually enjoy the job again."
                </p>
                <p className="text-sm font-semibold text-gray-900">— M.F., North Coast tourism industry</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Immediate Support Resources
            </h2>
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <p className="text-gray-700 mb-6">
                If you're experiencing a mental health crisis or need urgent support, please contact:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Lifeline NI:</span>
                  <span>0808 808 8000 (24/7 crisis helpline)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Samaritans:</span>
                  <span>116 123 (24/7 listening service)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Causeway Coast & Glens Crisis Support:</span>
                  <span>Contact Causeway Coast & Glens Borough Council for local mental health services</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Emergency:</span>
                  <span>If in immediate danger, call 999 or go to A&E</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 mt-6">
                Hypnotherapy is not a substitute for emergency mental health care or treatment of diagnosed mental illness. If you have suicidal thoughts or are in crisis, please seek immediate professional help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default PortrushHypnotherapy;
